import { Check, Copy, Edit, Link2, Package, Plus, Save, Search, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  useAddProductsToLink,
  useCreateCustomLink,
  useCustomLinks,
  useDeleteCustomLink,
  useRemoveProductsFromLink,
  useUpdateCustomLink
} from '../hooks/useCustomLinks';
import useStoreConfig from '../hooks/useStoreConfig';
import { ProductService } from '../services/productService';
import useToastStore from '../store/useToastStore';
import { CustomLink } from '../types/customLink';
import { Product } from '../types/product';

const CreateLinkPage: React.FC = () => {
  const { showToast } = useToastStore();
  const { data: storeData } = useStoreConfig();

  // Custom Links state
  const { customLinks, isLoading: linksLoading, refetch: refetchLinks } = useCustomLinks();
  const { createCustomLink, isCreating } = useCreateCustomLink();
  const { updateCustomLink, isUpdating } = useUpdateCustomLink();
  const { deleteCustomLink, isDeleting } = useDeleteCustomLink();
  const { addProductsToLink, isAdding } = useAddProductsToLink();
  const { removeProductsFromLink, isRemoving } = useRemoveProductsFromLink();

  // UI state
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState<CustomLink | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setIsSearching(true);
      const results = await ProductService.searchProducts(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching products:', error);
      showToast('Failed to search products', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const addProduct = (product: Product) => {
    if (!selectedProducts.find(p => p._id === product._id)) {
      setSelectedProducts([...selectedProducts, product]);
      showToast(`Added ${product.title}`, 'success');
    } else {
      showToast('Product already added', 'error');
    }
  };

  const removeProduct = (productId: string) => {
    setSelectedProducts(selectedProducts.filter(p => p._id !== productId));
    showToast('Product removed', 'success');
  };


  const handleCreateLink = () => {
    if (!newLinkTitle.trim()) {
      showToast('Please enter a title', 'error');
      return;
    }

    if (selectedProducts.length === 0) {
      showToast('Please select at least one product', 'error');
      return;
    }

    createCustomLink({
      title: newLinkTitle.trim(),
      products: selectedProducts.map(p => p._id),
      isActive: true,
    });

    // Reset form
    setNewLinkTitle('');
    setSelectedProducts([]);
    setSearchQuery('');
    setSearchResults([]);
    setShowForm(false);
  };

  const handleUpdateLink = () => {
    if (!editingLink) return;

    updateCustomLink({
      id: editingLink._id!.toString(),
      data: {
        title: editingLink.title,
        products: selectedProducts.map(p => p._id),
      }
    });

    setEditingLink(null);
    setSelectedProducts([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCreateNewLink = () => {
    setEditingLink(null);
    setSelectedProducts([]);
    setNewLinkTitle('');
    setSearchQuery('');
    setSearchResults([]);
    setShowForm(true);
  };

  const handleEditLink = (link: CustomLink) => {
    setEditingLink(link);
    setSelectedProducts(link.products);
    setShowForm(true);
  };

  const handleDeleteLink = (id: string) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      deleteCustomLink(id);
    }
  };

  const handleAddProductToLink = (linkId: string, productId: string) => {
    addProductsToLink({ id: linkId, productIds: [productId] });
  };

  const handleRemoveProductFromLink = (linkId: string, productId: string) => {
    removeProductsFromLink({ id: linkId, productIds: [productId] });
  };

  const generateDynamicLink = (slug: string) => {
    // Check if running in development mode
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

    let baseUrl;
    if (isDevelopment) {
      baseUrl = 'http://localhost:3000';
    } else {
      const customDomain = (storeData as any)?.customDomain;
      const storeUrl = (storeData as any)?.storeUrl;

      if (customDomain) {
        baseUrl = `https://${customDomain}`;
      } else {
        baseUrl = `https://${storeUrl}.mart-24.com`;
      }
    }

    return `${baseUrl}/add-to-cart/${slug}`;
  };

  const copyToClipboard = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      showToast('Link copied to clipboard!', 'success');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      showToast('Failed to copy link', 'error');
    }
  };

  const clearForm = () => {
    setSelectedProducts([]);
    setSearchQuery('');
    setSearchResults([]);
    setNewLinkTitle('');
    setEditingLink(null);
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 truncate">
                  {showForm ? (editingLink ? 'Edit Link' : 'Create New Link') : 'Custom Links'}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  {showForm
                    ? (editingLink ? 'Update your custom link details' : 'Create a new shareable link')
                    : 'Create and manage shareable links with multiple products'
                  }
                </p>
              </div>
            </div>
            {!showForm && (
              <button
                onClick={handleCreateNewLink}
                className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-indigo-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all duration-200 flex-shrink-0"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Create New Link</span>
                <span className="sm:hidden">Create</span>
              </button>
            )}
          </div>
        </div>

        {showForm ? (
          /* Create/Edit Link Form */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Search & Results */}
            <div className="lg:col-span-2 space-y-4">
              {/* Link Details */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">
                  {editingLink ? 'Edit Link Details' : 'Link Details'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Title *
                    </label>
                    <input
                      type="text"
                      value={editingLink ? editingLink.title : newLinkTitle}
                      onChange={(e) => {
                        if (editingLink) {
                          setEditingLink({ ...editingLink, title: e.target.value });
                        } else {
                          setNewLinkTitle(e.target.value);
                        }
                      }}
                      placeholder="e.g., Summer Collection"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Search Box */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Add Products</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900">
                      Search Results ({searchResults.length})
                    </h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                      {searchResults.map((product) => (
                        <div
                          key={product._id}
                          className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:border-indigo-200 hover:bg-indigo-50 transition-all duration-200"
                        >
                          <img
                            src={product.images[0] || '/placeholder-product.png'}
                            alt={product.title}
                            className="w-10 h-10 object-cover rounded-md"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {product.title}
                            </h4>
                            <p className="text-xs text-gray-500">₹{product.discountedPrice || product.mrp}</p>
                          </div>
                          <button
                            onClick={() => addProduct(product)}
                            disabled={selectedProducts.some(p => p._id === product._id)}
                            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${selectedProducts.some(p => p._id === product._id)
                              ? 'bg-green-100 text-green-600 cursor-not-allowed'
                              : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                              }`}
                          >
                            {selectedProducts.some(p => p._id === product._id) ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Selected Products & Actions */}
            <div className="space-y-4">
              {/* Selected Products */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    Selected Products ({selectedProducts.length})
                  </h3>
                  {selectedProducts.length > 0 && (
                    <button
                      onClick={clearForm}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="p-4">
                  {selectedProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No products selected</p>
                      <p className="text-xs text-gray-400 mt-1">Search and add products</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedProducts.map((product, index) => (
                        <div
                          key={product._id}
                          className="flex items-center gap-3 p-2 border border-gray-100 rounded-lg group hover:border-gray-200"
                        >
                          <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-indigo-600">{index + 1}</span>
                          </div>
                          <img
                            src={product.images[0] || '/placeholder-product.png'}
                            alt={product.title}
                            className="w-8 h-8 object-cover rounded-md"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-medium text-gray-900 truncate">
                              {product.title}
                            </h4>
                            <p className="text-xs text-gray-500">₹{product.discountedPrice || product.mrp}</p>
                          </div>
                          <button
                            onClick={() => removeProduct(product._id)}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  {selectedProducts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <button
                        onClick={editingLink ? handleUpdateLink : handleCreateLink}
                        disabled={isCreating || isUpdating}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50"
                      >
                        {isCreating || isUpdating ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {editingLink ? 'Update Link' : 'Create Link'}
                      </button>

                      <button
                        onClick={clearForm}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        ) : (
          /* Links List */
          <div className="space-y-4">
            {linksLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading links...</p>
              </div>
            ) : customLinks.length === 0 ? (
              <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200">
                <Link2 className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No custom links yet</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Create your first shareable link to get started</p>
                <button
                  onClick={handleCreateNewLink}
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-indigo-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                  Create Link
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {customLinks.map((link) => {
                  const dynamicLink = generateDynamicLink(link.slug);
                  return (
                    <div key={link._id?.toString()} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{link.title}</h3>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            <button
                              onClick={() => copyToClipboard(dynamicLink)}
                              className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${copiedLink === dynamicLink
                                ? 'bg-green-100 text-green-600'
                                : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                                }`}
                            >
                              {copiedLink === dynamicLink ? (
                                <Check className="w-3 h-3" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                              <span className="hidden sm:inline">Copy Link</span>
                              <span className="sm:hidden">Copy</span>
                            </button>
                            <button
                              onClick={() => handleEditLink(link)}
                              className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:px-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline ml-1">Edit</span>
                            </button>
                            <button
                              onClick={() => handleDeleteLink(link._id!.toString())}
                              disabled={isDeleting}
                              className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:px-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-all duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="hidden sm:inline ml-1">Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900">
                            Products ({link.products.length})
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                          {link.products.map((product) => (
                            <div key={product._id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border border-gray-100 rounded-lg">
                              <img
                                src={product.images[0] || '/placeholder-product.png'}
                                alt={product.title}
                                className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded-md flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <h5 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                  {product.title}
                                </h5>
                                <p className="text-xs text-gray-500">₹{product.discountedPrice || product.mrp}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateLinkPage; 